
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown, X, Search, Filter } from "lucide-react";
import { cn } from "@/lib/utils";

// Define specialties array with proper initialization and validation
const SPECIALTIES = [
  "Terapia Cognitivo-Comportamental",
  "Psicanálise", 
  "Psicologia Clínica",
  "Terapia Familiar",
  "Psicologia Infantil",
  "Neuropsicologia",
  "Psicologia Organizacional",
  "Terapia de Casal",
  "Psicologia do Esporte",
  "Psicologia Escolar",
  "Psicologia Jurídica",
  "Sexologia",
  "Gerontopsicologia",
  "Psicoterapia Online",
  "Avaliação Psicológica",
  "Orientação Profissional",
  "Psicologia Positiva",
  "Coaching Psicológico",
  "Hipnoterapia",
  "EMDR",
  "Terapia do Trauma",
  "Mindfulness",
  "Ansiedade",
  "Depressão",
  "Transtornos Alimentares",
  "Dependência Química",
  "Luto e Perdas",
  "Autoestima",
  "Relacionamentos",
  "Estresse"
].filter(specialty => specialty && typeof specialty === 'string' && specialty.trim() !== "").sort();

interface PsychologistFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedSpecialties: string[];
  onSpecialtiesChange: (specialties: string[]) => void;
  priceRange: string;
  onPriceRangeChange: (range: string) => void;
  onClearFilters: () => void;
}

const PsychologistFilters = ({
  searchQuery = "",
  onSearchChange,
  selectedSpecialties = [],
  onSpecialtiesChange,
  priceRange = "all",
  onPriceRangeChange,
  onClearFilters
}: PsychologistFiltersProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Ensure selectedSpecialties is always a valid array with safe defaults
  const safeSelectedSpecialties = Array.isArray(selectedSpecialties) 
    ? selectedSpecialties.filter(s => s && typeof s === 'string' && s.trim().length > 0)
    : [];

  // Filter specialties based on search term
  const filteredSpecialties = SPECIALTIES.filter(specialty =>
    specialty.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSpecialtySelect = (specialty: string) => {
    if (!specialty || typeof specialty !== 'string' || specialty.trim().length === 0) {
      return;
    }
    
    if (safeSelectedSpecialties.includes(specialty)) {
      const newSpecialties = safeSelectedSpecialties.filter(s => s !== specialty);
      onSpecialtiesChange(newSpecialties);
    } else {
      const newSpecialties = [...safeSelectedSpecialties, specialty];
      onSpecialtiesChange(newSpecialties);
    }
  };

  const removeSpecialty = (specialty: string) => {
    if (!specialty || typeof specialty !== 'string') {
      return;
    }
    
    const newSpecialties = safeSelectedSpecialties.filter(s => s !== specialty);
    onSpecialtiesChange(newSpecialties);
  };

  const hasActiveFilters = searchQuery || safeSelectedSpecialties.length > 0 || priceRange !== "all";

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input 
          placeholder="Buscar por nome ou especialidade" 
          value={searchQuery || ''}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Specialty Filter */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="justify-between min-w-[200px]"
            >
              <Filter className="mr-2 h-4 w-4" />
              {safeSelectedSpecialties.length === 0 ? (
                "Filtrar por especialidade"
              ) : safeSelectedSpecialties.length === 1 ? (
                "1 especialidade"
              ) : (
                `${safeSelectedSpecialties.length} especialidades`
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0" align="start">
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar especialidade..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="max-h-[300px] overflow-y-auto">
              {filteredSpecialties.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Nenhuma especialidade encontrada.
                </div>
              ) : (
                <div className="p-2">
                  {filteredSpecialties.map((specialty) => (
                    <button
                      key={specialty}
                      onClick={() => {
                        handleSpecialtySelect(specialty);
                        setSearchTerm("");
                      }}
                      className={cn(
                        "w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded-md hover:bg-accent transition-colors",
                        safeSelectedSpecialties.includes(specialty) && "bg-accent"
                      )}
                    >
                      <Check
                        className={cn(
                          "h-4 w-4",
                          safeSelectedSpecialties.includes(specialty) ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {specialty}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* Price Range Filter */}
        <Select value={priceRange || 'all'} onValueChange={onPriceRangeChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Faixa de preço" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os preços</SelectItem>
            <SelectItem value="0-100">Até R$ 100</SelectItem>
            <SelectItem value="100-200">R$ 100 - R$ 200</SelectItem>
            <SelectItem value="200-300">R$ 200 - R$ 300</SelectItem>
            <SelectItem value="300+">Acima de R$ 300</SelectItem>
          </SelectContent>
        </Select>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button variant="ghost" onClick={onClearFilters} className="text-muted-foreground">
            Limpar Filtros
          </Button>
        )}
      </div>

      {/* Selected Specialty Badges */}
      {safeSelectedSpecialties.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {safeSelectedSpecialties.map((specialty) => (
            <Badge 
              key={`badge-${specialty}`}
              variant="secondary" 
              className="flex items-center gap-1 px-3 py-1"
            >
              {specialty}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 ml-1 hover:bg-transparent"
                onClick={() => removeSpecialty(specialty)}
              >
                <X className="h-3 w-3 cursor-pointer hover:text-destructive" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export default PsychologistFilters;
